import {
    computed,
    inject,
    runInInjectionContext,
    type Injector,
    type Signal,
} from '@angular/core';
import {
    assertInjector,
    type QueryStatus,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import type { Observable } from 'rxjs';
import { filter } from 'rxjs';
import { hasOrgPermission, type HasPermissionType } from '../permissions';
import type { CurrentUser } from './models';
import { UserStore } from './user.store';

export interface InjectUser {
    /** Returns user data or null */
    (): CurrentUser | null;
    /** Observable of user data */
    $: Observable<CurrentUser | null>;
    /** Observable of user data filtered to non-null values (for route-guarded components where user must be loaded) */
    required$: Observable<CurrentUser>;

    // Query-aligned status signals
    status: Signal<QueryStatus>;
    isIdle: Signal<boolean>;
    isLoading: Signal<boolean>;
    isSuccess: Signal<boolean>;
    isError: Signal<boolean>;
    isFetched: Signal<boolean>;
    error: Signal<ResolvedProblemDetails | null>;

    /** Asserts user is loaded - throws if not */
    required: () => CurrentUser;

    hasPermission: (permissions: HasPermissionType) => Signal<boolean>;
    /** User initials extracted from name (first letter of first and second word if available) */
    initials: Signal<string>;
}

export function injectUser(injector?: Injector): InjectUser {
    const resolvedInjector = assertInjector(injectUser, injector);

    const userStore = runInInjectionContext(resolvedInjector, () => {
        return inject(UserStore);
    });

    const user = userStore.me; // Already a signal

    const required = () => {
        const u = user();
        if (!u) {
            throw new TypeError('User is either not loaded or not found');
        }
        return u;
    };

    const hasPermissionFn = (permissions: HasPermissionType) =>
        computed(() => {
            const u = user();
            return u ? hasOrgPermission(u, permissions) : false;
        });

    const initials = computed(() => {
        const u = user();
        if (!u?.displayName) return '';

        let initials = '';
        const splitArr = u.displayName.split(' ');
        if (splitArr.length) {
            initials = splitArr[0].substring(0, 1);
        }
        if (splitArr.length > 1) {
            initials = `${initials}${splitArr[1].substring(0, 1)}`;
        }
        return initials;
    });

    const fn = (() => user()) as InjectUser;
    fn.$ = userStore.me$;
    fn.required$ = userStore.me$.pipe(
        filter((u): u is CurrentUser => u !== null)
    );
    fn.status = userStore.meQuery.status;
    fn.isIdle = userStore.meQuery.isIdle;
    fn.isLoading = userStore.meQuery.isLoading;
    fn.isSuccess = userStore.meQuery.isSuccess;
    fn.isError = userStore.meQuery.isError;
    fn.isFetched = userStore.meQuery.isFetched;
    fn.error = userStore.meQuery.error;
    fn.required = required;
    fn.hasPermission = hasPermissionFn;
    fn.initials = initials;
    return fn;
}
